import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

type AppVariant = 'primary' | 'secondary' | 'ghost';
type AppSize = 'sm' | 'md' | 'lg';

export interface MuiButtonProps extends Omit<ButtonProps, 'variant' | 'size' | 'color'> {
  variant?: AppVariant;
  size?: AppSize;
  isLoading?: boolean;
  icon?: React.ReactNode;
}

const SIZE_SX: Record<AppSize, object> = {
  sm: { px: 2, py: 1, fontSize: '0.875rem' },
  md: { px: 3, py: 1.25, fontSize: '0.875rem' },
  lg: { px: 4, py: 1.5, fontSize: '1rem' },
};

const VARIANT_SX: Record<AppVariant, object> = {
  primary: {
    color: '#fff',
    backgroundImage: 'linear-gradient(to right, #4F46E5, #9333EA)',
    boxShadow: '0 10px 15px -3px rgba(99,102,241,0.3)',
    '&:hover': { backgroundImage: 'linear-gradient(to right, #4338CA, #7E22CE)', boxShadow: '0 20px 25px -5px rgba(99,102,241,0.4)' },
    '&.Mui-disabled': { color: '#fff', opacity: 0.5 },
  },
  secondary: {
    color: 'text.primary',
    backgroundColor: 'background.paper',
    border: '2px solid',
    borderColor: 'divider',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  ghost: {
    color: 'primary.main',
    px: 0,
    py: 0,
    minWidth: 0,
    '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
  },
};

/**
 * MUI-first sibling of components/common/atoms/AppButton.tsx — same
 * variant/size/isLoading/icon vocabulary, but always renders through real
 * MUI (Button + theme tokens) instead of AppButton's plain-<button>
 * fallback for the legacy vocabulary.
 */
export const MuiButton: React.FC<MuiButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  disabled,
  children,
  sx,
  ...rest
}) => (
  <Button
    disabled={isLoading || disabled}
    disableRipple={variant === 'ghost'}
    sx={{
      borderRadius: variant === 'ghost' ? 0 : '12px', // rounded-xl
      textTransform: 'none',
      fontWeight: 600,
      transition: 'all 0.2s ease',
      ...(variant !== 'ghost' ? SIZE_SX[size] : {}),
      ...VARIANT_SX[variant],
      ...sx,
    }}
    {...rest}
  >
    {isLoading && <CircularProgress size={18} sx={{ color: 'inherit', mr: 1.5 }} />}
    {!isLoading && icon && (
      <Box component="span" sx={{ display: 'inline-flex', mr: 1.5 }}>
        {icon}
      </Box>
    )}
    {children}
  </Button>
);

export default MuiButton;
